import { Input, Switch, AutoComplete, Select, Tooltip, Modal, Spin } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components/macro'
import { resetVenueProducts } from 'src/features/VenueProductsSlice'
import DeliveryDays from 'src/components/DeliveryDaysField'
import {
  clearXeroData,
  fetchXeroOrganizationData,
  searchXeroContacts,
} from 'src/features/XeroIntegrationSlice'

import { LockOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { ReactMultiEmail, isEmail } from 'react-multi-email'
import {
  ORDER_CUTOFF_INSTRUCTIONS_HELPER_TEXT,
  ORDER_CUTOFF_INSTRUCTIONS_LABEL,
  ORDER_CUTOFF_TIME_LABEL,
} from 'src/utils/supplierOrderCutOff'
import {
  XERO_AUTO_SEND_SUPPLIER_TOGGLE_HINT,
  XERO_AUTO_SEND_SUPPLIER_TOGGLE_LABEL,
  buildIntegrationsPath,
  buildXeroAutoSendSupplierUpdate,
  readXeroAutoSendEnabled,
  readXeroAutoSendEnabledForSupplier,
} from 'src/utils/xeroAutoSendCopy'
import {
  REQUIRE_GOODS_RECEIPT_TOGGLE_DESCRIPTION,
  REQUIRE_GOODS_RECEIPT_TOGGLE_HINT,
  REQUIRE_GOODS_RECEIPT_TOGGLE_LABEL,
} from 'src/utils/goodsReceiptReorderCopy'
import {
  resolveXeroOrganization,
  getXeroMappingMismatch,
  XERO_MAPPING_OTHER_ORG_WARNING,
  XERO_CONTACT_ARCHIVED_WARNING,
} from 'src/utils/xeroOrganization'
import { fetchVenueSupplierPriceFiles } from 'src/features/VenueSuppliersSlice'
import { mergeVenueSupplierDraft } from 'src/utils/venueSupplierDraft'
import {
  VENUE_DELIVERY_FEE_HELP,
  VENUE_DELIVERY_FEE_LABEL,
} from 'src/utils/deliveryFeeCopy'

const getModalPopupContainer = trigger =>
  trigger?.closest('.ant-modal') || trigger?.parentNode || document.body

const EditVenueSupplierModal = ({
  venueSupplier,
  account,
  setEditingVenueSupplier,
  onShowUpgradeModal,
  venueId,
}) => {
  const {
    accountNumber,
    minOrderValue,
    // deliveryFee,
    deliveryDays,
    orderCutOffTime,
    isActive,
    isCatalogueAccessible,
    manualOrdersOnly,
  } = venueSupplier

  // Keep editable pounds separate from the API/state amount in pennies.
  // A text value preserves intermediate decimal input such as "75.".
  const [minimumOrderInput, setMinimumOrderInput] = useState(
    String(Number(minOrderValue || 0) / 100),
  )
  const [deliveryFeeInput, setDeliveryFeeInput] = useState(
    (Number(venueSupplier.deliveryFee || 0) / 100).toFixed(2),
  )
  useEffect(() => {
    setMinimumOrderInput(current =>
      Math.round(Number(current || 0) * 100) === Number(minOrderValue || 0)
        ? current
        : String(Number(minOrderValue || 0) / 100),
    )
  }, [minOrderValue])
  useEffect(() => {
    const stored = (Number(venueSupplier.deliveryFee || 0) / 100).toFixed(2)
    setDeliveryFeeInput(current =>
      Math.round(Number(current || 0) * 100) ===
      Number(venueSupplier.deliveryFee || 0)
        ? current
        : stored,
    )
  }, [venueSupplier.deliveryFee])

  // Shared with Xero mapping + booking-off: never spread the venueSupplier prop
  // alone — that drops earlier toggles/picks before re-render (Chloe's report).
  const patchEditingVenueSupplier = patch => {
    setEditingVenueSupplier(prev =>
      mergeVenueSupplierDraft(prev, venueSupplier, patch),
    )
  }

  const isAdmin = account && account.isAdmin
  const isTFRAdmin = account && account.TFRAdmin
  const isJustAdmin = account && account.JUSTAdmin
  const isGreenAdmin = account && account.GREENAdmin
  const supplierCutOffTime = String(
    venueSupplier?.supplier?.orderCutOffTime ?? '',
  ).trim()
  const canEditVenueSupplierSettings =
    isAdmin || isTFRAdmin || isJustAdmin || isGreenAdmin

  const dispatch = useDispatch()
  const venue = useSelector(state => state.venue.venue)
  const usageLimits = useSelector(state => state.venue?.venue?.usageLimits)
  const venueSuppliers = useSelector(
    state => state.venueSuppliers.venueSuppliers,
  )
  const xero = venue?.xeroConnection
  const [searchContact, setSearchContact] = useState('')
  const [selctedOrganization, setSelectedOrganization] = useState(null)
  const [selectedXeroAccount, setSelectedXeroAccount] = useState(null)
  const [selectedXeroContact, setSelectedXeroContact] = useState(null)
  const [filteredAccountOptions, setFilteredAccountOptions] = useState([])
  const [skipNextContactSearch, setSkipNextContactSearch] = useState(false)
  // Ant Design AutoComplete fires onSearch synchronously after onSelect.
  // A ref (not useState) can block that echo in the same event so the pick
  // is not cleared immediately.
  const ignoreNextContactSearchRef = useRef(false)
  const [isSearchingContacts, setIsSearchingContacts] = useState(false)
  const [priceFiles, setPriceFiles] = useState([])
  const [priceFilesLoading, setPriceFilesLoading] = useState(false)
  const xeroIntegration = useSelector(state => state.xeroIntegration) || {
    accounts: [],
    contacts: [],
    accountsLoading: false,
    contactsLoading: false,
    isLoading: false,
    error: null,
  }

  const currentSupplierId = String(
    venueSupplier?.supplier?._id || venueSupplier?.supplier || '',
  )
  const selectedPriceFileId =
    venueSupplier?.pendingPriceFileId || currentSupplierId

  useEffect(() => {
    let cancelled = false
    const loadPriceFiles = async () => {
      if (!canEditVenueSupplierSettings || !venueId || !venueSupplier?._id) {
        return
      }
      setPriceFilesLoading(true)
      const result = await dispatch(
        fetchVenueSupplierPriceFiles(venueId, venueSupplier._id),
      )
      if (!cancelled) {
        setPriceFiles(result?.priceFiles || [])
        setPriceFilesLoading(false)
      }
    }
    loadPriceFiles()
    return () => {
      cancelled = true
    }
  }, [canEditVenueSupplierSettings, venueId, venueSupplier?._id, dispatch])

  // Default to resolved destination organisation
  useEffect(() => {
    if (!xero?.organizations?.length || selctedOrganization) return
    const resolved = resolveXeroOrganization(xero)
    if (resolved) setSelectedOrganization(resolved)
  }, [xero, selctedOrganization])

  useEffect(() => {
    if (selctedOrganization) {
      // Use Redux state for accounts
      setFilteredAccountOptions(xeroIntegration?.accounts || [])
    }
  }, [selctedOrganization, xeroIntegration?.accounts, xero?.organizations])

  // Fetch organization data when organization is selected
  useEffect(() => {
    if (selctedOrganization?.id) {
      dispatch(fetchXeroOrganizationData(selctedOrganization.id, venue?._id))
    }
  }, [selctedOrganization?.id, dispatch, venue?._id])

  // Debounced contact search against API (same behaviour as SendToXero)
  useEffect(() => {
    // If we just selected a contact, skip one effect run
    if (skipNextContactSearch) {
      setSkipNextContactSearch(false)
      return
    }

    // If input is empty, don't search
    if (!searchContact.trim()) {
      return
    }

    // Need venue and organization to search
    if (!venue?._id || !selctedOrganization?.id) {
      return
    }

    setIsSearchingContacts(true)
    const handler = setTimeout(async () => {
      try {
        await dispatch(
          searchXeroContacts(
            selctedOrganization.id,
            venue._id,
            searchContact.trim(),
          ),
        )
      } catch (error) {
        console.error('Contact search error:', error)
      } finally {
        setIsSearchingContacts(false)
      }
    }, 600) // call API after 600ms of inactivity

    return () => {
      clearTimeout(handler)
    }
  }, [
    searchContact,
    dispatch,
    selctedOrganization?.id,
    venue?._id,
    skipNextContactSearch,
  ])

  // Cleanup Redux state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearXeroData())
    }
  }, [dispatch])

  useEffect(() => {
    if (venueSupplier.defaultAccount) {
      const selectedAccount = filteredAccountOptions.find(
        account => account.AccountID === venueSupplier.defaultAccount,
      )
      if (selectedAccount) {
        setSelectedXeroAccount(selectedAccount)
      }
    }
    if (venueSupplier.defaultContact) {
      let selectedContact
      if (venueSupplier.defaultContactDetails) {
        selectedContact = venueSupplier.defaultContactDetails
      } else {
        selectedContact = xeroIntegration.contacts.find(
          contact => contact.ContactID === venueSupplier.defaultContact,
        )
      }
      if (selectedContact) {
        setSelectedXeroContact(selectedContact)
      }
    }
  }, [venueSupplier, filteredAccountOptions, xeroIntegration.contacts])

  const mappingMismatch = getXeroMappingMismatch({
    defaultContact: venueSupplier?.defaultContact,
    defaultAccount: venueSupplier?.defaultAccount,
    contacts: xeroIntegration?.contacts,
    accounts: filteredAccountOptions,
  })
  const contactArchived =
    selectedXeroContact?.ContactStatus === 'ARCHIVED' ||
    venueSupplier?.defaultContactDetails?.ContactStatus === 'ARCHIVED'

  const [manualOrdersModalVisible, setManualOrdersModalVisible] =
    useState(false)

  const handleToggleManualOrders = checked => {
    if (checked) {
      setManualOrdersModalVisible(true)
    } else {
      patchEditingVenueSupplier({ manualOrdersOnly: false })
    }
  }

  const confirmEnableManualOrders = () => {
    patchEditingVenueSupplier({ manualOrdersOnly: true })
    setManualOrdersModalVisible(false)
  }

  const handleRestrictedToggle = checked => {
    const currentPlan = usageLimits?.currentPlan
    const restrictedLimit =
      usageLimits?.features?.Restricted_Suppliers?.limit ?? 1

    const otherRestrictedCount = venueSuppliers.filter(
      vs => vs._id !== venueSupplier._id && vs.restricted === true,
    ).length

    const resultingRestrictedCount = otherRestrictedCount + (checked ? 1 : 0)

    if (
      checked &&
      currentPlan === 'Free' &&
      resultingRestrictedCount > restrictedLimit
    ) {
      if (onShowUpgradeModal) {
        onShowUpgradeModal({
          supplierTitle: venueSupplier.supplierTitle,
        })
      }
      return
    }

    // When enabling and there are no approval emails yet, prefill with venue owner email if available
    let nextApprovalEmails = venueSupplier.approvalEmails || []
    if (
      checked &&
      (!nextApprovalEmails || nextApprovalEmails.length === 0) &&
      venue?.account?.emailAddress
    ) {
      nextApprovalEmails = [venue.account.emailAddress]
    }

    patchEditingVenueSupplier({
      restricted: checked,
      approvalEmails: nextApprovalEmails,
    })
  }

  return (
    <Wrapper>
      {canEditVenueSupplierSettings && (
        <Row>
          <PriceFileBlock>
            <PriceFileLabel>
              Price file{' '}
              <Tooltip title='Switch this venue onto another published price file for the same supplier. Supplier Portal price files appear here once created.'>
                <InfoCircleOutlined
                  style={{ marginLeft: 4, color: '#1890ff' }}
                />
              </Tooltip>
            </PriceFileLabel>
            <Select
              showSearch
              getPopupContainer={getModalPopupContainer}
              optionFilterProp='children'
              loading={priceFilesLoading}
              value={selectedPriceFileId || undefined}
              placeholder='Select a price file'
              style={{ width: '100%' }}
              onChange={value => {
                patchEditingVenueSupplier({ pendingPriceFileId: value })
              }}
              notFoundContent={
                priceFilesLoading ? 'Loading…' : 'No price files found'
              }
            >
              {priceFiles.map(priceFile => {
                const label = [priceFile.title, priceFile.secondTitle]
                  .filter(Boolean)
                  .join(' — ')
                return (
                  <Select.Option
                    key={priceFile._id}
                    value={String(priceFile._id)}
                  >
                    {label}
                  </Select.Option>
                )
              })}
            </Select>
            {priceFiles.length > 1 && (
              <HelperText>
                {priceFiles.length} price files available for this supplier
              </HelperText>
            )}
          </PriceFileBlock>
        </Row>
      )}
      <Row>
        <InputComp
          value={accountNumber}
          onChange={e => {
            patchEditingVenueSupplier({ accountNumber: e.target.value })
          }}
          addonBefore={'OMS account number'}
        />
      </Row>
      <Row>
        <InputComp
          value={venueSupplier.sageSupplierAccountNumber || ''}
          onChange={e => {
            patchEditingVenueSupplier({
              sageSupplierAccountNumber: e.target.value,
            })
          }}
          addonBefore={
            <span>
              Sage Contact Number{' '}
              <Tooltip title='If you use Sage 50, enter this supplier’s Sage contact number. We’ll include it in the export so invoices map to the correct contact in Sage.'>
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
            </span>
          }
        />
      </Row>
      <Row>
        <InputComp
          value={minimumOrderInput}
          disabled={
            isAdmin || isTFRAdmin || isJustAdmin || isGreenAdmin ? false : true
          }
          onChange={e => {
            const value = e.target.value
            if (!/^\d*(\.\d{0,2})?$/.test(value)) return
            setMinimumOrderInput(value)
            patchEditingVenueSupplier({
              minOrderValue: Math.round(
                Number(value === '.' ? 0 : value) * 100,
              ),
            })
          }}
          addonBefore={'Minimum order value'}
        />
      </Row>
      <Row>
        <InputComp
          value={deliveryFeeInput}
          disabled={!canEditVenueSupplierSettings}
          onChange={e => {
            const value = e.target.value
            if (!/^\d*(\.\d{0,2})?$/.test(value)) return
            setDeliveryFeeInput(value)
            patchEditingVenueSupplier({
              deliveryFee: Math.round(Number(value === '.' ? 0 : value) * 100),
            })
          }}
          addonBefore={VENUE_DELIVERY_FEE_LABEL}
          data-testid='edit-supplier-delivery-fee'
        />
      </Row>
      <HelperText>{VENUE_DELIVERY_FEE_HELP}</HelperText>
      <Row>
        <DeliveryDays
          deliveryDays={deliveryDays}
          isDisabled={!canEditVenueSupplierSettings}
          onChange={value => {
            patchEditingVenueSupplier({ deliveryDays: value })
          }}
        />
      </Row>
      <InstructionsRow>
        <InputComp
          value={orderCutOffTime}
          onChange={e => {
            patchEditingVenueSupplier({ orderCutOffTime: e.target.value })
          }}
          addonBefore={ORDER_CUTOFF_INSTRUCTIONS_LABEL}
          disabled={!canEditVenueSupplierSettings}
        />
        <HelperText>{ORDER_CUTOFF_INSTRUCTIONS_HELPER_TEXT}</HelperText>
        <HelperText>
          {ORDER_CUTOFF_TIME_LABEL}:{' '}
          {supplierCutOffTime || 'Not set on supplier'}
        </HelperText>
      </InstructionsRow>
      {/* Show Supplier Toggle - Available to all users */}
      <ToggleRow>
        <ToggleLabel>
          <Tooltip
            title={
              manualOrdersOnly
                ? 'Controlled by Manual orders only.'
                : "If disabled, this supplier won't appear in the shopping list and no orders can be placed."
            }
          >
            <span>
              Show Supplier{' '}
              {manualOrdersOnly && <LockOutlined style={{ marginLeft: 6 }} />}
              <InfoCircleOutlined style={{ marginLeft: 6, color: '#1890ff' }} />
            </span>
          </Tooltip>
        </ToggleLabel>
        <Switch
          checked={isActive}
          disabled={manualOrdersOnly}
          onChange={() => {
            patchEditingVenueSupplier(prev => ({
              isActive: !prev.isActive,
            }))
            dispatch(resetVenueProducts())
          }}
        />
      </ToggleRow>
      <ToggleDescription>
        Turning this off will hide the supplier in the app & Dashboard shopping
        list – staff won&apos;t be able to place orders with them.
      </ToggleDescription>

      {/* Catalogue Access Toggle - Available to all users */}
      <ToggleRow>
        <ToggleLabel>
          <Tooltip
            title={
              manualOrdersOnly
                ? 'Controlled by Manual orders only.'
                : "If disabled, app & Dashboard users won't see the full product catalogue – they'll only see items on their shopping list."
            }
          >
            <span>
              Catalogue Access{' '}
              {manualOrdersOnly && <LockOutlined style={{ marginLeft: 6 }} />}
              <InfoCircleOutlined style={{ marginLeft: 6, color: '#1890ff' }} />
            </span>
          </Tooltip>
        </ToggleLabel>
        <Switch
          checked={isCatalogueAccessible}
          disabled={manualOrdersOnly}
          onChange={() => {
            patchEditingVenueSupplier(prev => ({
              isCatalogueAccessible: !prev.isCatalogueAccessible,
            }))
          }}
        />
      </ToggleRow>
      <ToggleDescription>
        Turning this off limits app users to only the items added to the
        shopping list.
      </ToggleDescription>

      {/* Manual Orders Only Toggle - Available to all users */}
      <ToggleRow>
        <ToggleLabel>
          <Tooltip
            title={
              'If enabled, you will only be able to place manual orders and attach invoices in Goods Receipting - keeping stats accurate.'
            }
          >
            <span>
              Manual Order Only{' '}
              <InfoCircleOutlined style={{ marginLeft: 6, color: '#1890ff' }} />
            </span>
          </Tooltip>
        </ToggleLabel>
        <Switch
          checked={manualOrdersOnly}
          onChange={handleToggleManualOrders}
        />
      </ToggleRow>
      <ToggleDescription>
        Turning this on will mean the supplier won&apos;t appear anywhere on
        OMS, however, you will still be able to add manual orders and attach
        invoices. Best for suppliers who don&apos;t work with OMS (e.g. Tesco).
      </ToggleDescription>

      {/* Restricted Supplier Toggle */}
      <ToggleRow>
        <ToggleLabel>
          <Tooltip
            title={
              'When enabled, any order placed with this supplier must be approved by management before being sent. Orders auto-reject after 48 hours.'
            }
          >
            <span>
              Restricted Supplier
              <InfoCircleOutlined style={{ marginLeft: 6, color: '#1890ff' }} />
            </span>
          </Tooltip>
        </ToggleLabel>
        <Switch
          checked={!!venueSupplier.restricted}
          onChange={handleRestrictedToggle}
        />
      </ToggleRow>
      {venueSupplier.restricted && (
        <>
          <ToggleDescription>
            When this is on, team members can still create orders to this
            supplier, but OMS will hold them as &apos;Approval Pending&apos;
            until a manager approves via email. Approval requests will be
            emailed to these addresses whenever an order is placed.
          </ToggleDescription>
          <Row>
            <Div>
              <Span>Approval email addresses</Span>
              <ReactMultiEmail
                placeholder='Enter email addresses'
                emails={venueSupplier.approvalEmails || []}
                onChange={_emails => {
                  patchEditingVenueSupplier({ approvalEmails: _emails })
                }}
                validateEmail={email => isEmail(email)}
                getLabel={(email, index, removeEmail) => (
                  <InnerDiv data-tag key={index}>
                    {email}
                    <span data-tag-handle onClick={() => removeEmail(index)}>
                      ×
                    </span>
                  </InnerDiv>
                )}
              />
            </Div>
          </Row>
        </>
      )}

      {/* Require goods receipt before reorder — default OFF */}
      <ToggleRow>
        <ToggleLabel>
          <Tooltip title={REQUIRE_GOODS_RECEIPT_TOGGLE_HINT}>
            <span>
              {REQUIRE_GOODS_RECEIPT_TOGGLE_LABEL}{' '}
              <InfoCircleOutlined style={{ marginLeft: 6, color: '#1890ff' }} />
            </span>
          </Tooltip>
        </ToggleLabel>
        <Switch
          checked={!!venueSupplier.requireGoodsReceiptBeforeReorder}
          onChange={checked => {
            patchEditingVenueSupplier({
              requireGoodsReceiptBeforeReorder: checked,
            })
          }}
        />
      </ToggleRow>
      <ToggleDescription>
        {REQUIRE_GOODS_RECEIPT_TOGGLE_DESCRIPTION}
      </ToggleDescription>

      {manualOrdersOnly && (
        <WarningBanner>
          ⚠️ This supplier is set to <strong>Manual orders only</strong>. Team
          members cannot place orders. Still available in Manual Order and
          Invoice uploads.
        </WarningBanner>
      )}
      {xero && (
        <XeroSection>
          <XeroSectionHeader>
            <XeroSectionTitle>Xero supplier settings</XeroSectionTitle>
            <XeroSectionDescription>
              These settings only apply to your connected Xero account. Choose
              where this supplier’s bills should be posted, then decide whether
              reconciled bills auto-send.
            </XeroSectionDescription>
          </XeroSectionHeader>
          <XeroFieldRow>
            <XeroFieldLabel>Xero organisation</XeroFieldLabel>
            {xero?.organizations?.length > 1 ? (
              <Select
                getPopupContainer={getModalPopupContainer}
                style={{ width: '100%' }}
                placeholder='Select a Xero organisation'
                value={selctedOrganization?.id}
                onChange={organizationId => {
                  if (organizationId) {
                    const org = xero?.organizations?.find(
                      o => o.id === organizationId,
                    )
                    setSelectedOrganization(org || null)
                  } else {
                    setSelectedOrganization(resolveXeroOrganization(xero))
                  }
                }}
              >
                {xero?.organizations.map(organization => (
                  <Select.Option value={organization.id} key={organization.id}>
                    {organization?.name}
                  </Select.Option>
                ))}
              </Select>
            ) : (
              <XeroFieldValue>
                {selctedOrganization?.name ||
                  resolveXeroOrganization(xero)?.name ||
                  xero?.organizations?.[0]?.name ||
                  '—'}
              </XeroFieldValue>
            )}
          </XeroFieldRow>

          {selctedOrganization ? (
            <>
              <XeroFieldRow>
                <XeroFieldLabel>Purchases account</XeroFieldLabel>
                {/*
                  Use Select (not AutoComplete): two stacked AutoCompletes in an
                  antd Modal cause click-through — mousedown closes the account
                  dropdown and the click lands on the contact field underneath.
                */}
                <Select
                  showSearch
                  allowClear
                  getPopupContainer={trigger => trigger.parentNode}
                  style={{ width: '100%' }}
                  placeholder={
                    xeroIntegration?.accountsLoading
                      ? 'Loading accounts...'
                      : 'Select a purchases account'
                  }
                  disabled={xeroIntegration?.accountsLoading}
                  optionFilterProp='children'
                  value={selectedXeroAccount?.AccountID || undefined}
                  filterOption={(input, option) => {
                    const label = option?.props?.children
                    return String(label || '')
                      .toLowerCase()
                      .includes(String(input || '').toLowerCase())
                  }}
                  onChange={value => {
                    if (!value) {
                      setSelectedXeroAccount(null)
                      patchEditingVenueSupplier({
                        defaultAccount: '',
                      })
                      return
                    }
                    const selectedAccount = filteredAccountOptions.find(
                      account => account.AccountID === value,
                    )
                    setSelectedXeroAccount(selectedAccount || null)
                    patchEditingVenueSupplier({
                      defaultAccount: selectedAccount?.AccountID || '',
                    })
                  }}
                >
                  {filteredAccountOptions?.map(account => (
                    <Select.Option
                      value={account?.AccountID}
                      key={`xero-account-${account?.AccountID}`}
                    >
                      {account?.Name}
                    </Select.Option>
                  ))}
                </Select>
              </XeroFieldRow>

              {selectedXeroAccount?.TaxType === 'OUTPUT2' &&
                selectedXeroAccount?.Type != 'REVENUE' && (
                  <WarningBanner>
                    ⚠️ Heads up! This Xero account is set to{' '}
                    <strong>VAT on Income</strong>,so we cant send VAT info
                    through. To include VAT data, youll need to change the tax
                    settings in Xero.
                  </WarningBanner>
                )}

              {selectedXeroAccount?.Type === 'REVENUE' && (
                <WarningBanner>
                  ⚠️ Heads up! The selected Xero account is of type{' '}
                  <strong>Revenue</strong>. You can’t create a bill using a
                  Revenue account in Xero. Please either select a different
                  account or change this account’s type in Xero (for example, to
                  an Expense account) before continuing.
                </WarningBanner>
              )}

              <XeroFieldRow>
                <XeroFieldLabel>Supplier / contact</XeroFieldLabel>
                <AutoComplete
                  getPopupContainer={trigger => trigger.parentNode}
                  style={{ width: '100%' }}
                  value={searchContact || selectedXeroContact?.Name || ''}
                  onSelect={contactId => {
                    const contact = xeroIntegration.contacts.find(
                      c => c.ContactID === contactId,
                    )
                    ignoreNextContactSearchRef.current = true
                    setSelectedXeroContact(contact || null)
                    setSearchContact(contact?.Name || '')
                    setSkipNextContactSearch(true)
                    // Functional update: do not spread venueSupplier prop — that
                    // drops a prior contact/account pick made in the same edit.
                    patchEditingVenueSupplier({
                      defaultContact: contact?.ContactID || '',
                      defaultContactDetails: contact || null,
                    })
                  }}
                  onSearch={value => {
                    if (ignoreNextContactSearchRef.current) {
                      ignoreNextContactSearchRef.current = false
                      return
                    }
                    setSelectedXeroContact(null)
                    setSearchContact(value)
                  }}
                  placeholder='Search supplier / contact...'
                  loading={isSearchingContacts}
                  notFoundContent={
                    isSearchingContacts ? (
                      <Spin size='small' />
                    ) : (
                      'Contacts not found'
                    )
                  }
                >
                  {xeroIntegration.contacts.map(contact => (
                    <AutoComplete.Option
                      key={`xero-contact-${contact.ContactID}`}
                      value={contact.ContactID}
                      // Prevent mousedown blur so the click does not fall through
                      // onto the account Select underneath when the menu closes.
                      onMouseDown={e => e.preventDefault()}
                    >
                      {contact.Name}
                    </AutoComplete.Option>
                  ))}
                </AutoComplete>
              </XeroFieldRow>
            </>
          ) : null}

          {mappingMismatch.hasMismatch ? (
            <MappingWarn>{XERO_MAPPING_OTHER_ORG_WARNING}</MappingWarn>
          ) : null}
          {contactArchived ? (
            <MappingWarn>{XERO_CONTACT_ARCHIVED_WARNING}</MappingWarn>
          ) : null}

          <ToggleRow>
            <ToggleLabel>
              <Tooltip title={XERO_AUTO_SEND_SUPPLIER_TOGGLE_HINT}>
                <span>
                  {XERO_AUTO_SEND_SUPPLIER_TOGGLE_LABEL}{' '}
                  <InfoCircleOutlined
                    style={{ marginLeft: 6, color: '#1890ff' }}
                  />
                </span>
              </Tooltip>
            </ToggleLabel>
            <Switch
              checked={readXeroAutoSendEnabledForSupplier(venueSupplier)}
              onChange={checked => {
                patchEditingVenueSupplier({
                  ...buildXeroAutoSendSupplierUpdate(checked),
                })
              }}
            />
          </ToggleRow>
          <ToggleDescription>
            {XERO_AUTO_SEND_SUPPLIER_TOGGLE_HINT}{' '}
            {!readXeroAutoSendEnabled(venue) ? (
              <>
                Venue Auto-send is currently OFF —{' '}
                <Link to={buildIntegrationsPath(venue?._id)}>
                  manage in Integrations
                </Link>
                .
              </>
            ) : null}
          </ToggleDescription>
        </XeroSection>
      )}

      <Modal
        visible={manualOrdersModalVisible}
        title='Switch to Manual orders only?'
        onCancel={() => setManualOrdersModalVisible(false)}
        onOk={confirmEnableManualOrders}
        okText='Confirm'
        cancelText='Cancel'
      >
        <p>
          We’ll hide <strong>{venueSupplier?.supplierTitle}</strong> from
          ordering for <strong>{venue?.title}</strong>. Any open baskets will be
          cleared.
        </p>
        <p>You can still create Manual Orders and upload invoices.</p>
      </Modal>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const InputComp = styled(Input)``

const Row = styled.div`
  display: flex;
  margin: 15px 0;
  justify-content: space-between;
`

const PriceFileBlock = styled.div`
  width: 100%;
`

const PriceFileLabel = styled.div`
  margin-bottom: 6px;
  font-weight: 500;
`

const InstructionsRow = styled.div`
  margin: 15px 0;
`

const HelperText = styled.div`
  margin-top: 4px;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 12px;
`

const ToggleRow = styled.div`
  display: flex;
  margin: 15px 0;
  justify-content: space-between;
  align-items: center;
`

const ToggleLabel = styled.div`
  display: flex;
  align-items: center;
  font-weight: 500;
`

const ToggleDescription = styled.div`
  color: #666;
  font-size: 12px;
  margin: -10px 0 15px 0;
  padding-left: 0;
  line-height: 1.4;
`

const XeroSection = styled.section`
  margin-top: 22px;
  padding: 16px 12px 4px;
  border-top: 3px solid #13b5ea;
  border-radius: 4px;
  background: #f4fbfe;
`

const XeroSectionHeader = styled.div`
  margin-bottom: 10px;
`

const XeroSectionTitle = styled.h3`
  margin: 0 0 4px;
  color: #087eae;
  font-size: 16px;
`

const XeroSectionDescription = styled.p`
  margin: 0;
  color: #4f6570;
  font-size: 12px;
  line-height: 1.45;
`

const XeroFieldRow = styled.div`
  margin: 10px 0 12px;
  position: relative;
`

const XeroFieldLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #444;
  margin-bottom: 6px;
`

const XeroFieldValue = styled.div`
  font-size: 14px;
  color: #222;
  padding: 6px 0;
`

const WarningBanner = styled.div`
  background-color: #fffbe6;
  color: #ad8b00;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  padding: 12px;
  margin: 8px 0;
  font-size: 11px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

const MappingWarn = styled(WarningBanner)`
  color: #a8071a;
  background-color: #fff2f0;
  border-color: #ffccc7;
`

const Div = styled.div`
  width: 100%;
`

const Span = styled.span`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`

const InnerDiv = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  margin: 2px;
  background-color: #f0f0f0;
  border-radius: 12px;
  font-size: 12px;
`

export default EditVenueSupplierModal
